import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import {
  CombinedCondition,
  FilterModel,
  SimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import { isCombinedCondition, isSimpleCondition } from "@app/util/viewModel/assert";
import { areSimpleConditionsEqual } from "@app/util/viewModel/comparators";

const getEffectiveConditionCount = (
  condition: CombinedCondition,
  excludeFieldNames: Set<IField["filterName"]>,
  excludeFieldsUpToLevel: number
): number =>
  condition.conditions.reduce((result, condition) => {
    if (isSimpleCondition(condition)) {
      return excludeFieldNames.has(condition.fieldFilterName) ? result : result + 1;
    }
    if (isCombinedCondition(condition)) {
      return (
        result +
        getEffectiveConditionCount(
          condition,
          excludeFieldsUpToLevel ? excludeFieldNames : new Set(),
          excludeFieldsUpToLevel - 1
        )
      );
    }
    return result;
  }, 0);

export const getCombinedFilterDifferencesCount = (
  filters1: CombinedCondition,
  filters2: CombinedCondition,
  excludeFieldNames: Set<IField["filterName"]>,
  excludeFieldsUpToLevel = 1
): number => {
  let result = filters1.operator === filters2.operator ? 0 : 1;

  // Separate conditions by type
  const simple1 = filters1.conditions.filter(isSimpleCondition) as SimpleCondition[];
  const simple2 = filters2.conditions.filter(isSimpleCondition) as SimpleCondition[];
  const combined1 = filters1.conditions.filter(isCombinedCondition) as CombinedCondition[];
  const combined2 = filters2.conditions.filter(isCombinedCondition) as CombinedCondition[];

  // Simple conditions: two-pass greedy matching
  const matchedSimple1Indices = new Set<number>();
  const matchedSimple2Indices = new Set<number>();

  // Pass 1: exact match (via areSimpleConditionsEqual)
  // Prevents exact-equal conditions from being "stolen" by
  // same-field-different-value matches (see phase 2)
  // This phase doesn't add anything to diffs count
  for (let i = 0; i < simple1.length; i++) {
    if (excludeFieldNames.has(simple1[i].fieldFilterName)) {
      continue;
    }

    const matchIdx = simple2.findIndex(
      (c2, j) => !matchedSimple2Indices.has(j) && areSimpleConditionsEqual(simple1[i], c2)
    );

    if (matchIdx >= 0) {
      matchedSimple1Indices.add(i);
      matchedSimple2Indices.add(matchIdx);
    }
  }

  // Pass 2: remaining unmatched — match by fieldFilterName only
  // We add such conditions to diff count
  for (let i = 0; i < simple1.length; i++) {
    if (matchedSimple1Indices.has(i) || excludeFieldNames.has(simple1[i].fieldFilterName)) {
      continue;
    }

    // same field, different operator/value
    const matchIdx = simple2.findIndex(
      (c2, j) => !matchedSimple2Indices.has(j) && c2.fieldFilterName === simple1[i].fieldFilterName
    );

    if (matchIdx >= 0) {
      matchedSimple1Indices.add(i);
      matchedSimple2Indices.add(matchIdx);
      result++;
    } else {
      result++;
    }
  }

  // Unmatched simple conditions in filters2 (added)
  for (let i = 0; i < simple2.length; i++) {
    if (!matchedSimple2Indices.has(i) && !excludeFieldNames.has(simple2[i].fieldFilterName)) {
      result++;
    }
  }

  // Combined conditions, pass 1: greedy best-match with 0-diff short-circuit
  const matchedI = new Set<number>();
  const matchedJ = new Set<number>();
  const pairDiffs: Array<{ diff: number; i: number; j: number }> = [];

  for (let i = 0; i < combined1.length; i++) {
    for (let j = 0; j < combined2.length; j++) {
      if (matchedJ.has(j)) {
        continue;
      }

      // excludeFieldNames not passed deeper (up to excludeFieldsUpToLevel only)
      const diff = getCombinedFilterDifferencesCount(
        combined1[i],
        combined2[j],
        excludeFieldsUpToLevel ? excludeFieldNames : new Set(),
        excludeFieldsUpToLevel - 1
      );
      if (!diff) {
        matchedI.add(i);
        matchedJ.add(j);
        // Exact match, skip remaining candidates in combined2
        break;
      }
      pairDiffs.push({ diff, i, j });
    }
  }

  // Combined conditions, pass 2: For remaining, greedily assign minimum-diff pairs
  pairDiffs.sort((a, b) => a.diff - b.diff);
  for (const pair of pairDiffs) {
    if (matchedI.has(pair.i) || matchedJ.has(pair.j)) {
      continue;
    }
    matchedI.add(pair.i);
    matchedJ.add(pair.j);
    result += pair.diff;
  }

  // Add unmatched combined conditions, counting only non-excluded inner conditions
  const innerExcludeFieldNames: Set<IField["filterName"]> = excludeFieldsUpToLevel
    ? excludeFieldNames
    : new Set();
  const innerLevel = excludeFieldsUpToLevel - 1;
  for (let i = 0; i < combined1.length; i++) {
    if (!matchedI.has(i)) {
      result += getEffectiveConditionCount(combined1[i], innerExcludeFieldNames, innerLevel);
    }
  }
  for (let j = 0; j < combined2.length; j++) {
    if (!matchedJ.has(j)) {
      result += getEffectiveConditionCount(combined2[j], innerExcludeFieldNames, innerLevel);
    }
  }

  return result;
};

export const getFilterDifferencesCount = (
  filters1: FilterModel,
  filters2: FilterModel,
  excludeFields?: IField[],
  // Default 1 propagates simple condition exclusions through one nested combined (top level simple conditions + simple conditions in top-level combined conditions).
  excludeFieldsUpToLevel = 1
): number => {
  const excludedFieldsNames = new Set((excludeFields ?? []).map(({ filterName }) => filterName));
  return getCombinedFilterDifferencesCount(
    filters1,
    filters2,
    excludedFieldsNames,
    excludeFieldsUpToLevel
  );
};
